// components/RoleSelection.tsx
// Role selection component for post-registration flow
'use client'

import React from 'react'
import { UserRole } from '@/types'

interface RoleSelectionProps {
  onSelect: (role: UserRole) => void
  loading: boolean
  message?: string
  messageType?: 'error' | 'success' | 'info'
}

export default function RoleSelection({ onSelect, loading, message, messageType }: RoleSelectionProps) {
  const roles = [
    {
      id: 'landlord' as UserRole,
      title: 'Landlord',
      description: 'List properties and connect with tenants',
      icon: (
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      color: 'emerald',
      benefits: ['List unlimited properties', 'Verified landlord badge', 'Direct tenant messaging']
    },
    {
      id: 'client' as UserRole,
      title: 'Renter',
      description: 'Find and rent your perfect home',
      icon: (
        <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      color: 'amber',
      benefits: ['Browse verified properties', 'Save favorite listings', 'Inquire directly with landlords']
    }
  ]

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-2xl font-display text-stone-900 mb-2">Choose Your Role</h2>
        <p className="text-stone-500">
          Select how you'll use LandLordNoAgent
        </p>
      </div>

      {/* Role options */}
      <div className="space-y-4">
        {roles.map((role) => (
          <button
            key={role.id}
            onClick={() => onSelect(role.id)}
            disabled={loading}
            className={`w-full p-5 border-2 rounded-2xl text-left transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg ${
              role.color === 'emerald'
                ? 'border-stone-200 hover:border-emerald-500 hover:bg-emerald-50/50'
                : 'border-stone-200 hover:border-amber-500 hover:bg-amber-50/50'
            }`}
          >
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                role.color === 'emerald'
                  ? 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white'
                  : 'bg-amber-100 text-amber-600 group-hover:bg-amber-600 group-hover:text-white'
              }`}>
                {role.icon}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-semibold text-stone-900">{role.title}</h3>
                  <svg className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${
                    role.color === 'emerald' ? 'text-emerald-500' : 'text-amber-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p className="text-sm text-stone-500 mb-3">{role.description}</p>
                
                {/* Benefits list */}
                <div className="flex flex-wrap gap-2">
                  {role.benefits.map((benefit, i) => (
                    <span 
                      key={i} 
                      className={`text-xs px-2 py-1 rounded-full ${
                        role.color === 'emerald'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {benefit}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Message display */}
      {message && (
        <div className={`mt-6 p-4 rounded-xl text-sm font-medium ${
          messageType === 'error' 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : messageType === 'success'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message}
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="mt-6 flex items-center justify-center gap-2 text-stone-500">
          <div className="w-5 h-5 border-2 border-stone-300 border-t-emerald-600 rounded-full animate-spin"></div>
          <span className="text-sm">Setting up your account...</span>
        </div>
      )}
    </div>
  )
}
